import * as mergicianModule from "mergician";
import pLimit from "p-limit";
import path from "path";
import { createDependencyMap, type DependencyMap, type PackageInfos } from "workspace-tools";
import { builtInTargetTypes } from "./builtInTargetTypes.js";
import { expandDepSpecs } from "./expandDepSpecs.js";
import { TargetFactory } from "./TargetFactory.js";
import { TargetGraphBuilder } from "./TargetGraphBuilder.js";
import { getPackageAndTask, getStagedTargetId, getTargetId } from "./targetId.js";
import type { Priority } from "./types/Priority.js";
import type { Target } from "./types/Target.js";
import type { TargetConfig } from "./types/TargetConfig.js";
import type { TargetGraph } from "./types/TargetGraph.js";

// mergician is a dual-mode library with CJS and ESM export but a single .d.ts file.
// Without type="module" on this packge.json typescript gets confused. See: https://github.com/microsoft/TypeScript/issues/50466
const mergician = mergicianModule.mergician || (mergicianModule as unknown as { default: typeof mergicianModule }).default.mergician;

/**
 * Maximum number of changed files for a staged target to run.
 * IF MODIFYING, UPDATE DOCS FOR `StagedTargetConfig.threshold`!
 */
const DEFAULT_STAGED_TARGET_THRESHOLD = 50;

/**
 * TargetGraphBuilder class provides a builder API for registering target configs. It exposes a method called `generateTargetGraph` to
 * generate a topological graph of targets (package + task) and their dependencies.
 *
 * Usage:
 *
 * ```typescript
 * const rootDir = process.cwd();
 * const packageInfos = getPackageInfos(rootDir);
 * const builder = new WorkspaceTargetGraphBuilder(rootDir, packageInfos);
 * const targetGraph = builder.buildTargetGraph([...packages], [...tasks]);
 * ```
 */
export class WorkspaceTargetGraphBuilder {
  /** A cache of the dependencyMap for packages, used inside dependency expansion */
  private dependencyMap: DependencyMap;

  private graphBuilder: TargetGraphBuilder;

  private targetFactory: TargetFactory;

  /** Whether at least one global/root target has been added */
  private hasRootTarget = false;

  /** Whether at least one relevant staged target has been added */
  private hasAnyStagedTarget = false;

  private targetConfigMap = new Map<string, TargetConfig>();

  /**
   * Initializes the builder with package infos
   */
  constructor(
    private options: {
      /** Root directory of the monorepo */
      root: string;
      /** Package infos for the monorepo */
      packageInfos: PackageInfos;
      enableTargetConfigMerging: boolean;
      enablePhantomTargetOptimization: boolean;
    }
  ) {
    this.dependencyMap = createDependencyMap(options.packageInfos, { withDevDependencies: true, withPeerDependencies: false });
    this.graphBuilder = new TargetGraphBuilder();
    this.targetFactory = new TargetFactory({
      root: options.root,
      packageInfos: options.packageInfos,
      resolve(packageName: string) {
        const pkg = options.packageInfos[packageName];
        if (!pkg?.packageJsonPath) {
          throw new Error(`Package "${packageName}" not found`);
        }
        return path.dirname(pkg.packageJsonPath);
      },
    });
  }

  /**
   * Generates new `Target`, indexed by the id based on a new target configuration.
   */
  public addTargetConfig(id: string, config: TargetConfig = {}, changedFiles?: string[]): void {
    // Generates a target definition from the target config
    if (id.startsWith("//") || id.startsWith("#")) {
      const targetConfig = this.determineFinalTargetConfig(id, config);
      const target = this.targetFactory.createGlobalTarget(id, targetConfig);
      this.graphBuilder.addTarget(target);
      this.hasRootTarget = true;

      this.processStagedConfig(target, config, changedFiles);
    } else if (id.includes("#")) {
      const { packageName, task } = getPackageAndTask(id);
      const targetConfig = this.determineFinalTargetConfig(id, config);
      const target = this.targetFactory.createPackageTarget(packageName!, task, targetConfig);
      this.graphBuilder.addTarget(target);

      this.processStagedConfig(target, config, changedFiles);
    } else {
      const packages = Object.keys(this.options.packageInfos);

      for (const packageName of packages) {
        const task = id;

        const targetConfig = this.determineFinalTargetConfig(getTargetId(packageName, task), config);
        const target = this.targetFactory.createPackageTarget(packageName!, task, targetConfig);
        this.graphBuilder.addTarget(target);

        this.processStagedConfig(target, config, changedFiles);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  private deepCloneTargetConfig: Function = mergician({
    appendArrays: true,
    onCircular: () => {
      throw new Error(`Circular object reference detected in TargetConfig`);
    },
  });

  /**
   * If `enableTargetConfigMerging`, merges `config` with any existing config for target `targetId`.
   * @param targetId The Id of the target to merge
   * @param config The TargetConfig settings that will be merged if this target has already been seen before
   * @returns The merged TargetConfig object.
   */
  private determineFinalTargetConfig(targetId: string, config: TargetConfig): TargetConfig {
    let finalConfig = config;
    if (this.options.enableTargetConfigMerging && this.targetConfigMap.has(targetId)) {
      const existingConfig = this.targetConfigMap.get(targetId)!;
      finalConfig = this.deepCloneTargetConfig(existingConfig, config);
    }

    this.targetConfigMap.set(targetId, finalConfig);
    return finalConfig;
  }

  /**
   * If `parentTarget` has a `stagedTarget` field and the number of `changedFiles` is below the
   * threshold, adds a staged version of this target with the same dependencies as `parentTarget`,
   * and makes `parentTarget` a no-op that depends on the staged target. This allows us to have a s
   * ingle task that runs over a list of changed files when running with `--since` flag, while still
   * preserving the original target for cases when we want to run it over all files.
   */
  private processStagedConfig(parentTarget: Target, config: TargetConfig, changedFiles: string[] | undefined): void {
    if (
      !config.stagedTarget ||
      !changedFiles?.length ||
      changedFiles.length > (config.stagedTarget.threshold ?? DEFAULT_STAGED_TARGET_THRESHOLD)
    ) {
      return;
    }

    this.hasAnyStagedTarget = true;

    // First convert the parent to be a NO-OP, not cached, and should never run
    parentTarget.type = builtInTargetTypes.noop;
    parentTarget.cache = false;
    parentTarget.shouldRun = false;

    // Create a staged target for the parent target
    const id = getStagedTargetId(parentTarget.task);
    const stagedTarget = this.graphBuilder.targets.has(id)
      ? this.graphBuilder.targets.get(id)!
      : this.targetFactory.createStagedTarget(parentTarget.task, config.stagedTarget, changedFiles);

    // Add the staged target to the graph
    this.graphBuilder.addTarget(stagedTarget);

    // Add all the parent target dependencies as the staged dependencies as unique set
    const depSet = new Set<string>(stagedTarget.dependencies);
    for (const dep of parentTarget.dependencies) {
      depSet.add(dep);
    }
    stagedTarget.dependencies = Array.from(depSet);

    // If parent target has dependents, we need to throw an error
    if (parentTarget.dependents.length > 0) {
      throw new Error(
        `Parent target ${parentTarget.id} cannot have dependents when it has a staged target while running with a --since flag`
      );
    }
  }

  public shouldRun(config: TargetConfig, target: Target): boolean | Promise<boolean> {
    if (typeof config.shouldRun === "function") {
      return config.shouldRun(target);
    }

    return true;
  }

  /**
   * Builds a scoped target graph for given tasks and packages
   *
   * Steps:
   * 1. expands the dependency specs from the target definitions
   * 2. sub-graph filtered from the full dependency graph
   * 3. filtering all targets to just only the ones listed in the sub-graph
   * 4. returns the sub-graph
   *
   * @param tasks Tasks for the graph
   * @param scope If provided, scope to these packages. Otherwise includes all packages.
   * @param priorities the set of global priorities for the workspace.
   */
  public async build(tasks: string[], scope?: string[], priorities?: Priority[]): Promise<TargetGraph> {
    scope ||= Object.keys(this.options.packageInfos);

    // Expands the dependency specs from the target definitions
    const fullDependencies = expandDepSpecs(
      this.graphBuilder.targets,
      this.dependencyMap,
      this.options.packageInfos,
      this.options.enablePhantomTargetOptimization
    );

    for (const [from, to] of fullDependencies) {
      this.graphBuilder.addDependency(from, to);
    }

    const subGraphEntries: string[] = [];

    for (const task of tasks) {
      for (const packageName of scope) {
        const targetId = getTargetId(packageName, task);

        if (this.options.enablePhantomTargetOptimization) {
          const target = this.graphBuilder.targets.get(targetId);
          if (target) {
            // Skip phantom npmScript targets: the package doesn't have this script
            if (target.type === builtInTargetTypes.npmScript) {
              const pkg = this.options.packageInfos[packageName];
              if (!pkg?.scripts?.[task]) {
                continue;
              }
            } else {
              // For non-npmScript targets (e.g. worker), skip if shouldRun resolves to false
              const config = this.targetConfigMap.get(targetId);
              if (config && typeof config.shouldRun === "function") {
                const result = await config.shouldRun(target);
                if (!result) {
                  continue;
                }
              }
            }
          }
        }

        subGraphEntries.push(targetId);
      }

      if (this.hasRootTarget) {
        const globalTargetId = getTargetId(undefined, task);
        if (this.graphBuilder.targets.has(globalTargetId)) {
          subGraphEntries.push(globalTargetId);
        }
      }

      if (this.hasAnyStagedTarget) {
        const stagedTargetId = getStagedTargetId(task);
        if (this.graphBuilder.targets.has(stagedTargetId)) {
          subGraphEntries.push(stagedTargetId);
        }
      }
    }

    // Add all the global priorities for individual targets
    if (priorities) {
      for (const priorityConfig of priorities) {
        // Right now we are only handling global priorities where the package name is set
        if (priorityConfig.package) {
          const targetId = getTargetId(priorityConfig.package, priorityConfig.task);
          const target = this.graphBuilder.targets.get(targetId);
          if (target) {
            target.priority = target.priority ? Math.max(target.priority, priorityConfig.priority) : priorityConfig.priority;
          }
        }
      }
    }

    const subGraph = this.graphBuilder.subgraph(subGraphEntries);

    const limit = pLimit(8);
    const setShouldRunPromises: Promise<void>[] = [];
    for (const target of subGraph.targets.values()) {
      const config = this.targetConfigMap.get(target.id);
      if (config) {
        setShouldRunPromises.push(
          limit(async () => {
            target.shouldRun = await this.shouldRun(config, target);
          })
        );
      }
    }

    await Promise.all(setShouldRunPromises);

    return {
      targets: subGraph.targets,
    };
  }
}
